/**
 * Client-side image compression for uploads that go through Cloudflare.
 * The raw phone-camera JPEG is often 3–5 MB, which frequently triggers a
 * 502/524 at the edge when the backend has to re-encode it before hitting
 * CJ's image-search endpoint. Downsampling to ~1024 px + JPEG 0.8 keeps
 * visual-search accuracy while cutting the payload to ~200–400 KB.
 *
 * Usage:
 *   const dataUrl = await compressImageToDataUrl(file, {
 *     maxDim: 1024,
 *     quality: 0.82,
 *     maxBytes: 900_000,
 *   });
 */
export async function compressImageToDataUrl(file, opts = {}) {
  const {
    maxDim = 1024,
    quality = 0.82,
    maxBytes = 900_000, // ~900 KB — well under Cloudflare's default JSON body cap
  } = opts;
  if (!file) throw new Error('No file provided');
  if (!file.type?.startsWith('image/')) {
    throw new Error('Not an image file');
  }

  // Try modern image bitmap path (fast, off main thread on most browsers)
  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    // Fallback for older Safari — decode via HTMLImageElement
    bitmap = await new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = (e) => {
        URL.revokeObjectURL(url);
        reject(e);
      };
      img.src = url;
    });
  }

  const w0 = bitmap.width || bitmap.naturalWidth;
  const h0 = bitmap.height || bitmap.naturalHeight;
  const scale = Math.min(1, maxDim / Math.max(w0, h0));
  const w = Math.round(w0 * scale);
  const h = Math.round(h0 * scale);

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff'; // flatten transparency for JPEG
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(bitmap, 0, 0, w, h);

  // Iteratively reduce quality until we fit under maxBytes.
  let q = quality;
  let dataUrl = canvas.toDataURL('image/jpeg', q);
  const bytesOf = (durl) => Math.floor((durl.length - durl.indexOf(',') - 1) * 0.75);
  while (bytesOf(dataUrl) > maxBytes && q > 0.35) {
    q = Math.max(0.35, q - 0.12);
    dataUrl = canvas.toDataURL('image/jpeg', q);
  }
  return dataUrl;
}


/**
 * Same client-side compression, but returns a real File (via Blob)
 * suitable for multipart/form-data uploads — used by the AI Estimate
 * wizard's photo picker so a 5 MB phone JPEG becomes a ~250 KB upload.
 */
export async function compressImageToFile(file, opts = {}) {
  const {
    maxDim = 1600,     // still sharp for AI vision + human review
    quality = 0.85,
    maxBytes = 900_000,
    targetType = 'image/jpeg',
  } = opts;
  if (!file) throw new Error('No file provided');
  if (!file.type?.startsWith('image/')) throw new Error('Not an image file');

  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    bitmap = await new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
      img.src = url;
    });
  }
  const w0 = bitmap.width || bitmap.naturalWidth;
  const h0 = bitmap.height || bitmap.naturalHeight;
  const scale = Math.min(1, maxDim / Math.max(w0, h0));
  const w = Math.round(w0 * scale);
  const h = Math.round(h0 * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(bitmap, 0, 0, w, h);

  // Iterative quality reduction using Blob for accurate byte counts.
  const toBlob = (q) => new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), targetType, q);
  });
  let q = quality;
  let blob = await toBlob(q);
  while (blob && blob.size > maxBytes && q > 0.35) {
    q = Math.max(0.35, q - 0.12);
    blob = await toBlob(q);
  }
  if (!blob) throw new Error('Could not encode image');

  // Preserve original name (swap extension to .jpg) so backend logs stay readable.
  const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
  const newName = `${baseName}.jpg`;
  return new File([blob], newName, { type: targetType, lastModified: Date.now() });
}
